import { useLayoutEffect, useRef, useState } from "react";

const CHART_ROOT_SELECTOR = ".futures-chart";
const CHART_TOOLBAR_SELECTOR = ".chart-toolbar";
const MAIN_BUTTON = 0;
// Mirrors $chart-overlay-offset in _variables.sass
const INITIAL_TOP_PX = 11;

const HIDDEN_STYLE = { visibility: "hidden" };

const clampPosition = ({ x, y }, { maxLeft, maxTop }) => ({
  x: Math.min(maxLeft, Math.max(0, x)),
  y: Math.min(maxTop, Math.max(0, y)),
});

// As in PO: at the top in the centre of the chart (spec §7.2). On a narrow chart that
// place is taken by the buttons of the chart toolbar, so it goes under them.
const measureBounds = (chartRoot, toolbar) => {
  const maxLeft = Math.max(0, chartRoot.clientWidth - toolbar.offsetWidth);
  const maxTop = Math.max(0, chartRoot.clientHeight - toolbar.offsetHeight);
  const initialLeft = Math.round(maxLeft / 2);
  const rootRect = chartRoot.getBoundingClientRect();
  const buttonsRect = chartRoot.querySelector(CHART_TOOLBAR_SELECTOR)?.getBoundingClientRect();
  const isOverButtons = Boolean(buttonsRect) && initialLeft < buttonsRect.right - rootRect.left;

  return {
    maxLeft,
    maxTop,
    initial: { x: initialLeft, y: isOverButtons ? buttonsRect.bottom - rootRect.top : INITIAL_TOP_PX },
  };
};

/**
 * Place of the floating drawing toolbar inside the chart root: the saved one or
 * the initial one, never past the chart edges, also after a resize (the saved
 * value stays). The handle drags it with a mouse or a finger; the place is saved
 * when the drag ends, a cancelled drag goes back.
 *
 * @param {object} params
 * @param {React.RefObject<HTMLElement>} params.toolbarRef - inside the chart root
 * @param {{ x: number, y: number }|null} params.savedPosition - CSS px of the chart
 * @param {(position: { x: number, y: number }) => void} params.onSave
 * @param {() => void} params.onDragStart
 * @returns {{ style: object, handleProps: object }} style: inline style of the toolbar, hidden
 *   until it is measured; left / top, not a transform: its fixed popovers count from the screen.
 *   handleProps: pointer handlers of the handle
 */
const useDrawingToolbarPosition = ({ toolbarRef, savedPosition, onSave, onDragStart }) => {
  const [bounds, setBounds] = useState(null);
  const [dragPosition, setDragPosition] = useState(null);
  // The pointer that drags, and where it holds the toolbar: { pointerId, offsetX, offsetY }
  const dragRef = useRef(null);

  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    const chartRoot = toolbar.closest(CHART_ROOT_SELECTOR);

    if (!chartRoot) return undefined;

    const measure = () => setBounds(measureBounds(chartRoot, toolbar));

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(chartRoot);
    // Its size depends on the screen: wider handle on phones
    observer.observe(toolbar);

    return () => observer.disconnect();
  }, [toolbarRef]);

  const position = bounds && clampPosition(dragPosition || savedPosition || bounds.initial, bounds);

  const positionAt = ({ clientX, clientY }) =>
    clampPosition({ x: clientX - dragRef.current.offsetX, y: clientY - dragRef.current.offsetY }, bounds);

  const isDragPointer = ({ pointerId }) => dragRef.current?.pointerId === pointerId;

  const handleProps = {
    onPointerDown: (event) => {
      if (!position || dragRef.current || event.button !== MAIN_BUTTON) return;

      // No text selection and no compatibility mouse events
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        offsetX: event.clientX - position.x,
        offsetY: event.clientY - position.y,
      };
      onDragStart();
    },

    onPointerMove: (event) => {
      if (isDragPointer(event)) setDragPosition(positionAt(event));
    },

    // A press without a move saves nothing: the toolbar stays on its initial place
    onPointerUp: (event) => {
      if (!isDragPointer(event)) return;

      if (dragPosition) onSave(positionAt(event));

      dragRef.current = null;
      setDragPosition(null);
    },

    onPointerCancel: (event) => {
      if (!isDragPointer(event)) return;

      dragRef.current = null;
      setDragPosition(null);
    },
  };

  return { style: position ? { left: position.x, top: position.y } : HIDDEN_STYLE, handleProps };
};

export default useDrawingToolbarPosition;
